/** The one way this app talks to a service (adapted from istok-classroom).
 *
 * Every call carries the bearer; a 401 buys exactly one refresh-and-retry before the session
 * is dropped. Errors arrive as ApiError so a screen can branch on the CODE the service sends
 * (`session_locked`, `not_released`, ...) rather than on a message.
 */
import { accessToken, dropSession, isLocalToken, refreshSession } from "../auth/session";
import { capabilities, type SaveResult } from "../platform/capabilities";
import { serviceUrl } from "./base";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
    readonly code?: string,
    readonly body?: unknown,
  ) {
    super(detail);
    this.name = "ApiError";
  }
}

async function parse(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function errorOf(status: number, body: unknown): ApiError {
  if (body && typeof body === "object") {
    const b = body as { detail?: unknown; code?: string };
    const detail =
      typeof b.detail === "string"
        ? b.detail
        : Array.isArray(b.detail)
          ? b.detail.map((d) => (d as { msg?: string }).msg ?? String(d)).join("; ")
          : `HTTP ${status}`;
    return new ApiError(status, detail, b.code, body);
  }
  return new ApiError(status, typeof body === "string" && body ? body : `HTTP ${status}`, undefined, body);
}

async function send(path: string, init: RequestInit, token: string | null): Promise<Response> {
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body !== undefined && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  return fetch(serviceUrl(path), { ...init, headers });
}

/** Call a service path (`/session/v1/...`). Throws ApiError on any non-2xx. */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res = await send(path, init, accessToken());
  if (res.status === 401 && (await refreshSession())) {
    res = await send(path, init, accessToken()); // one retry, never a loop
  }
  const body = await parse(res);
  if (!res.ok) {
    // A local token (host, paired phone) has nothing to refresh; a 401 means the cloud behind
    // the proxy rejected the call (code `cloud_signin`: nobody is signed in on this host), not
    // that the host session ended. Only a cloud token is dropped here.
    if (res.status === 401 && !isLocalToken(accessToken())) dropSession();
    throw errorOf(res.status, body);
  }
  return body as T;
}

const withBody = (method: string) => <T,>(path: string, body?: unknown) =>
  api<T>(path, { method, body: body === undefined ? undefined : JSON.stringify(body) });

export const get = <T,>(path: string) => api<T>(path);
export const post = withBody("POST");
export const put = withBody("PUT");
export const patch = withBody("PATCH");
export const del = <T,>(path: string) => api<T>(path, { method: "DELETE" });


/** Download a file from a service (QTI, CSV): bearer, then save.
 *
 * Guards the failure that shipped once: a 200 whose body is the app's own index.html,
 * saved as a .zip that will not open. Anything that is not the expected kind is an error the
 * screen can show, never a file on the teacher's disk.
 *
 * Saving goes through `capabilities.fileSave`, never through an anchor: in the desktop shell
 * `<a download>` is cancelled by the webview and the file never reaches disk. The caller gets
 * back where it went, so the screen can say so.
 */
export async function download(path: string, filename: string, expect: "zip" | "csv"): Promise<SaveResult> {
  const res = await send(path, {}, accessToken());
  if (!res.ok) throw errorOf(res.status, await parse(res));
  const type = (res.headers.get("content-type") ?? "").toLowerCase();
  if (type.includes("text/html")) throw new ApiError(res.status, `unexpected html for ${path}`, "not_a_file");
  const blob = expect === "csv"
    ? new Blob(["\ufeff" + (await res.text())], { type: "text/csv;charset=utf-8" }) // BOM: Excel reads Cyrillic
    : await res.blob();
  if (blob.size === 0) throw new ApiError(res.status, `empty file for ${path}`, "not_a_file");
  return capabilities.fileSave.save(filename, blob);
}
